import React, { useMemo, useState } from "react";
import Alert from "@mui/material/Alert";
import Box from "@mui/material/Box";
import Checkbox from "@mui/material/Checkbox";
import FormControlLabel from "@mui/material/FormControlLabel";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import LoadingButton from "@mui/lab/LoadingButton";
import { useNavigate, useSearchParams } from "react-router-dom";
import PageShell from "./shared/PageShell";
import { pageStyles } from "./shared/theme";

const EMAIL_TYPES = [
  {
    name: "Promotions",
    label: "Promotions",
    description: "Limited-time offers and promotional messages",
  },
  {
    name: "Surveys",
    label: "Surveys",
    description: "Invitations to share feedback with us",
  },
  {
    name: "Product Announcements",
    label: "Product announcements",
    description: "News and updates about Finn",
  },
];

const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

async function responseJson(response) {
  try {
    return await response.json();
  } catch (_error) {
    return {};
  }
}

export default function EmailPreferences() {
  const [searchParams] = useSearchParams();
  const requestedCategory = searchParams.get("category");
  const initialSelection = useMemo(
    () =>
      Object.fromEntries(
        EMAIL_TYPES.map(({ name }) => [
          name,
          requestedCategory === "all" || requestedCategory === name,
        ])
      ),
    [requestedCategory]
  );

  const [email, setEmail] = useState("");
  const [selected, setSelected] = useState(initialSelection);
  const [requestId, setRequestId] = useState("");
  const [otp, setOtp] = useState("");
  const [step, setStep] = useState("preferences");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const navigate = useNavigate();

  const selectedTypes = EMAIL_TYPES.filter(({ name }) => selected[name]);

  const requestedPreferences = () =>
    Object.fromEntries(selectedTypes.map(({ name }) => [name, false]));

  const requestCode = async () => {
    const response = await fetch(
      `${process.env.REACT_APP_API_BASE_URL}/api/v1/services/update-email-preferences`,
      {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          email: email.trim(),
          preferences: requestedPreferences(),
        }),
      }
    );
    const data = await responseJson(response);
    if (!response.ok || !data.requestId) {
      throw new Error(data.errorMessage || "Unable to send a verification code");
    }
    setRequestId(data.requestId);
    setOtp("");
    setStep("otp");
  };

  const handleRequestCode = async (event) => {
    event.preventDefault();
    setError("");

    if (!EMAIL_PATTERN.test(email.trim())) {
      setError("Enter a valid email address.");
      return;
    }
    if (selectedTypes.length === 0) {
      setError("Choose at least one type of email to unsubscribe from.");
      return;
    }

    try {
      setLoading(true);
      await requestCode();
    } catch (requestError) {
      setError(requestError.message);
    } finally {
      setLoading(false);
    }
  };

  const handleVerify = async (event) => {
    event.preventDefault();
    setError("");
    if (!/^\d{6}$/.test(otp)) {
      setError("Enter the six-digit code from your email.");
      return;
    }

    try {
      setLoading(true);
      const response = await fetch(
        `${process.env.REACT_APP_API_BASE_URL}/api/v1/services/verify-email-preferences`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ requestId, otp }),
        }
      );
      const data = await responseJson(response);
      if (!response.ok) {
        throw new Error(
          data.errorMessage || "The verification code is invalid or expired"
        );
      }
      navigate("/accounts/emailpreferences/success");
    } catch (verifyError) {
      setError(verifyError.message);
    } finally {
      setLoading(false);
    }
  };

  const handleResend = async () => {
    setError("");
    try {
      setLoading(true);
      await requestCode();
    } catch (requestError) {
      setError(requestError.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <PageShell>
      <Typography component="h1" variant="h1" sx={pageStyles.title}>
        {step === "preferences" ? "Email preferences" : "Check your email"}
      </Typography>
      <Typography variant="body1" sx={pageStyles.subtitle}>
        {step === "preferences"
          ? "Choose the marketing emails you no longer want to receive. Account and order updates will continue."
          : `We sent a six-digit verification code to ${email.trim()}.`}
      </Typography>

      {error ? <Alert severity="error" sx={{ mb: 2.5 }}>{error}</Alert> : null}

      {step === "preferences" ? (
        <Box component="form" onSubmit={handleRequestCode} noValidate>
          <TextField
            fullWidth
            id="email"
            label="Email address"
            name="email"
            autoComplete="email"
            value={email}
            onChange={(event) => {
              setEmail(event.target.value);
              setError("");
            }}
            sx={pageStyles.field}
          />

          <Typography variant="body2" sx={{ color: "#52667A", mb: 1.5 }}>
            Unsubscribe me from
          </Typography>
          {EMAIL_TYPES.map((type) => (
            <Box key={type.name} sx={pageStyles.option}>
              <FormControlLabel
                sx={{ alignItems: "flex-start", m: 0, width: "100%" }}
                control={
                  <Checkbox
                    checked={selected[type.name]}
                    onChange={(event) => {
                      setSelected((current) => ({
                        ...current,
                        [type.name]: event.target.checked,
                      }));
                      setError("");
                    }}
                    name={type.name}
                    sx={{ color: "#8DA0B2", pt: 0.25, "&.Mui-checked": { color: "#3856DD" } }}
                  />
                }
                label={
                  <Box sx={{ pt: 0.5 }}>
                    <Typography variant="body1" sx={{ color: "#102A43", lineHeight: 1.35 }}>
                      {type.label}
                    </Typography>
                    <Typography variant="body2" sx={{ color: "#61768A", mt: 0.25 }}>
                      {type.description}
                    </Typography>
                  </Box>
                }
              />
            </Box>
          ))}

          <LoadingButton
            type="submit"
            loading={loading}
            fullWidth
            variant="contained"
            sx={{ ...pageStyles.primaryButton, mt: 2 }}
          >
            Send verification code
          </LoadingButton>
        </Box>
      ) : (
        <Box component="form" onSubmit={handleVerify} noValidate>
          <TextField
            fullWidth
            autoFocus
            id="otp"
            label="Verification code"
            name="otp"
            value={otp}
            onChange={(event) => {
              setOtp(event.target.value.replace(/\D/g, "").slice(0, 6));
              setError("");
            }}
            inputProps={{ inputMode: "numeric", autoComplete: "one-time-code" }}
            sx={pageStyles.field}
          />
          <LoadingButton
            type="submit"
            loading={loading}
            fullWidth
            variant="contained"
            sx={pageStyles.primaryButton}
          >
            Verify and unsubscribe
          </LoadingButton>
          <Box sx={{ display: "flex", justifyContent: "center", gap: 1, mt: 2.25 }}>
            <LoadingButton
              loading={loading}
              onClick={handleResend}
              sx={{ color: "#3856DD", textTransform: "none" }}
            >
              Resend code
            </LoadingButton>
            <LoadingButton
              onClick={() => {
                setStep("preferences");
                setOtp("");
                setError("");
              }}
              sx={{ color: "#52667A", textTransform: "none" }}
            >
              Change email
            </LoadingButton>
          </Box>
        </Box>
      )}
    </PageShell>
  );
}
